import { MoreOutlined } from '@ant-design/icons';
import { Dropdown, Typography, Form } from 'antd';
import moment from 'moment';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { botFieldsConfig } from '@app//components/Modals/BotDetailsModal/configs';
import { useGetAiServicesQuery } from '@app/api/AiServices';
import { MESSAGE_MODE_AI } from '@app/components/MessageInputWithFilesUpload/data/messageMode';
import {
  INCOMING_MESSAGE_TRIGGER_TYPE,
  KEYWORD_MESSAGE_ROBOT_INDEX,
  SEND_MESSAGE_ROBOT_INDEX,
  SET_TAG_ROBOT_INDEX,
  TRANSITION_FROM_THE_SALES_FUNNEL_ROBOT_INDEX,
} from '@app/components/Modals/BotDetailsModal/constants/botIndexes';
import { botDetailsOptions } from '@app/components/Modals/BotDetailsModal/data';
import {
  FormFields,
  type BotDetailsFormInterface,
  type OverlapCheckGate,
  type RobotTagFilter,
  type UseBotDetailsModalProps,
} from '@app/components/Modals/BotDetailsModal/types';
import { getPublicationSelectionFormValues } from '@app/components/Modals/BotDetailsModal/utils/instagramPublications';
import { mapTriggerTagsFromApi } from '@app/components/Modals/BotDetailsModal/utils/triggerTagsGroups';
import { LAST_OWNER } from '@app/components/SalesFunnelColumnComponent/constants';
import { useSchedule } from '@app/components/Schedule';
import type { ServerScheduleData } from '@app/components/Schedule/types';
import {
  DEFAULT_SOCIAL_INTEGRATION_ID_INDEX,
  DEFAULT_CHANNEL_INDEX,
  LAST_INTEGRATION_PROP,
  LOYALTY_PROGRAM_SENDOUTS,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { ROBOT_REPLY_SERVICE_KEY } from '@app/pages/PaidAiFeatures/constants/aiServicesDictionary';

import type { CheckboxChangeEvent, FormRule } from 'antd';
import type { BaseOptionType } from 'antd/es/select';

const { Title } = Typography;
const { useForm } = Form;
const { CUSTOM_DEADLINE_ITEM_INDEX } = botDetailsOptions;

export const useBotDetailsModal = ({
  title,
  onDeleteBot,
  triggerList = [],
  onCancel,
  isOpen,
  isLoading,
  selectedBotTypeId = '',
  socialIntegrations,
  bot,
  selectedBotFiles,
  getSalesFunnelStagesListHandler,
  isUploadingAttachments,
}: UseBotDetailsModalProps) => {
  const defaultTriggerId = (
    triggerList.find(({ disabledReason }) => !disabledReason) ?? triggerList[0]
  )?.id;
  const [selectedTriggerId, setSelectedTriggerId] = useState<
    string | undefined
  >(defaultTriggerId);
  const [selectedDeadlineItem, setSelectedDeadlineItem] = useState<
    number | null
  >(null);
  const [taskSelectedDeadlineItem, setTaskSelectedDeadlineItem] = useState<
    number | null
  >(null);
  const [integrationsByBranch, setIntegrationsByBranch] = useState<
    BaseOptionType[]
  >([]);
  const [filteredIntegrations, setFilteredIntegrations] = useState<
    BaseOptionType[]
  >([]);
  const [tempTime, setTempTime] = useState({ h: 0, m: 0, s: 0 });
  const [taskTempTime, setTaskTempTime] = useState({ h: 0, m: 0, s: 0 });

  const [showSchedule, setShowSchedule] = useState(false);

  const [form] = useForm();
  const { submit } = form;
  const isNeedAddValue = Form.useWatch('isNeedAdd', form);

  const isSetTagIncomingMessageRobot =
    selectedBotTypeId === SET_TAG_ROBOT_INDEX &&
    selectedTriggerId === INCOMING_MESSAGE_TRIGGER_TYPE;

  // Один и тот же тег не может одновременно устанавливаться и сниматься
  const conflictingTagIdsRules: FormRule[] = [
    ({ getFieldValue }) => ({
      validator: (_, value: number[] | undefined) => {
        const setTagIds: number[] = getFieldValue('setTagIds') || [];
        const hasIntersection = (value || []).some((id) =>
          setTagIds.includes(id)
        );

        return hasIntersection
          ? Promise.reject(
              new Error(
                'Тег не может одновременно устанавливаться и сниматься — уберите его из одного из списков'
              )
            )
          : Promise.resolve();
      },
    }),
  ];

  const { initialScheduleValues, setScheduleFormData } = useSchedule();

  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const isAiCapableRobot =
    selectedBotTypeId === SEND_MESSAGE_ROBOT_INDEX ||
    selectedBotTypeId === KEYWORD_MESSAGE_ROBOT_INDEX;
  const { data: aiServices, refetch: refetchAiServices } =
    useGetAiServicesQuery(
      { ActiveBranchId },
      { skip: !ActiveBranchId || !isAiCapableRobot }
    );
  const isRobotReplyEnabled =
    aiServices?.some(
      ({ Key, IsOn }) => Key === ROBOT_REPLY_SERVICE_KEY && IsOn
    ) ?? false;

  useEffect(() => {
    if (isOpen && isAiCapableRobot && ActiveBranchId) {
      refetchAiServices();
    }
  }, [isOpen, isAiCapableRobot, ActiveBranchId, refetchAiServices]);

  useEffect(() => {
    if (isOpen && defaultTriggerId) {
      form.setFieldsValue({ trigger: defaultTriggerId });
      setSelectedTriggerId(defaultTriggerId);
    }
  }, [isOpen, defaultTriggerId, form]);

  useEffect(() => {
    setIntegrationsByBranch(socialIntegrations || []);
  }, [socialIntegrations]);

  // Проверка пересечений ключевых слов разрешена, пока окно открыто.
  // Каждое открытие — новая сессия: проверка, поставленная в прошлой
  // сессии, не уходит и после быстрого повторного открытия.
  const overlapCheckGateRef = useRef<OverlapCheckGate>({
    isAllowed: false,
    sessionId: 0,
  });

  // Номер открытия окна — состояние, а не только ref: при открытии
  // rc-dialog перерисовывает содержимое, и новый номер доходит до блока
  // проверки пропсом. Номер растёт на переходе «закрыто → открыто» прямо
  // при рендере (производное состояние React), без лишнего коммита.
  const [windowSessionId, setWindowSessionId] = useState(0);
  const [wasOpen, setWasOpen] = useState(isOpen);

  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);

    if (isOpen) {
      setWindowSessionId((sessionId) => sessionId + 1);
    }
  }

  // useLayoutEffect: он выполняется раньше обычных эффектов дочерних
  // компонентов, и блок проверки при открытии уже видит новую сессию
  useLayoutEffect(() => {
    overlapCheckGateRef.current.isAllowed = isOpen;
    overlapCheckGateRef.current.sessionId = windowSessionId;
  }, [isOpen, windowSessionId]);

  // Снять разрешение в момент закрытия, до сброса формы: сброс меняет
  // поля, и без этого блок отправил бы проверку пустого черновика
  const stopOverlapChecks = useCallback(() => {
    overlapCheckGateRef.current.isAllowed = false;
  }, []);

  const isAddButtonLoading = isLoading || isUploadingAttachments;

  const addButtonTooltip = isUploadingAttachments
    ? 'Происходит загрузка файлов'
    : undefined;

  const fieldsToDisplay = useMemo(() => {
    if (!isOpen || isLoading) return [];
    const botTypeKey = String(selectedBotTypeId);
    const triggerKey = String(selectedTriggerId || '') || defaultTriggerId;

    const fields = triggerKey
      ? botFieldsConfig[botTypeKey]?.[triggerKey]?.fields
      : undefined;
    return fields;
  }, [
    selectedBotTypeId,
    selectedTriggerId,
    isOpen,
    isLoading,
    defaultTriggerId,
  ]);

  const handleChannelOptions = useCallback(
    (fieldName: string) => {
      const socialIntegrationTypeValue = form.getFieldValue(
        'socialIntegrationType'
      );

      if (
        fieldName === 'socialIntegrationType' &&
        socialIntegrationTypeValue === DEFAULT_CHANNEL_INDEX
      ) {
        setFilteredIntegrations([LAST_INTEGRATION_PROP]);

        form.setFieldValue('socialIntegrationId', LAST_INTEGRATION_PROP.value);

        return;
      }

      if (
        fieldName === 'socialIntegrationType' &&
        socialIntegrationTypeValue === LOYALTY_PROGRAM_SENDOUTS
      ) {
        const loyaltyIntegration = integrationsByBranch.find(
          (integration) => integration.type === LOYALTY_PROGRAM_SENDOUTS
        );
        form.setFieldValue(
          'socialIntegrationId',
          loyaltyIntegration
            ? loyaltyIntegration.value
            : DEFAULT_SOCIAL_INTEGRATION_ID_INDEX
        );
        form.resetFields(['files']);
      }

      const socialType = form.getFieldValue(fieldName);
      const filteredIntegrations = socialType
        ? integrationsByBranch?.filter(({ type }) => type === socialType)
        : integrationsByBranch;

      setFilteredIntegrations([
        LAST_INTEGRATION_PROP,
        ...(filteredIntegrations || []),
      ]);

      return filteredIntegrations;
    },
    [form, integrationsByBranch]
  );

  const getBotDetailsModalTitle = useCallback(
    (type: 'create' | 'edit') => {
      if (type === 'create') {
        return title;
      }
      const titleWithDropdown = (
        <div className="title-with-dropdown-container">
          <Title level={5}>{title}</Title>
          <Dropdown
            placement="bottomLeft"
            menu={{
              items: [
                {
                  key: 'delete-bot',
                  label: 'Удалить робота',
                  onClick: onDeleteBot,
                },
              ],
            }}
          >
            <MoreOutlined />
          </Dropdown>
        </div>
      );
      return titleWithDropdown;
    },
    [onDeleteBot, title]
  );

  const handleTriggerChange = (value: string) => {
    setSelectedTriggerId(value);
  };

  const handleCancel = () => {
    stopOverlapChecks();
    form.resetFields();
    setSelectedTriggerId(undefined);
    setSelectedDeadlineItem(null);
    setTaskSelectedDeadlineItem(null);
    setTaskTempTime({ h: 0, m: 0, s: 0 });
    setTempTime({ h: 0, m: 0, s: 0 });
    onCancel();
  };

  const handleDeadlineChange = (value: number) => {
    setSelectedDeadlineItem(value);
    if (value !== CUSTOM_DEADLINE_ITEM_INDEX) {
      form.resetFields([
        FormFields.CustomDeadlineDay,
        FormFields.CustomDeadlineTime,
      ]);
    }
  };

  const handleTaskDeadlineChange = (value: number) => {
    setTaskSelectedDeadlineItem(value);
    if (value !== CUSTOM_DEADLINE_ITEM_INDEX) {
      form.resetFields([
        'task_custom_deadline_day',
        'task_custom_deadline_time',
      ]);
    }
  };

  const onCustomDayInput = (
    e: React.FormEvent<HTMLInputElement>,
    fieldName: string
  ) => {
    const newValue = (e.target as HTMLInputElement).value.replace(/\D/g, '');
    (e.target as HTMLInputElement).value = newValue;

    form.setFieldValue(fieldName, newValue);
  };
  const MIN_CUSTOM_DEADLINE_DAYS = 0;
  const MAX_CUSTOM_DEADLINE_DAYS = 180;

  const validateNumericInput = (value: string | undefined) => {
    if (value === undefined || value === null || value === '') {
      return Promise.resolve();
    }

    if (!/^\d+$/.test(value)) {
      return Promise.reject(new Error('Введите целое число'));
    }

    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return Promise.reject(new Error('Введите корректное число'));
    }

    if (
      numericValue < MIN_CUSTOM_DEADLINE_DAYS ||
      numericValue > MAX_CUSTOM_DEADLINE_DAYS
    ) {
      return Promise.reject(
        new Error(
          `Введите число от ${MIN_CUSTOM_DEADLINE_DAYS} до ${MAX_CUSTOM_DEADLINE_DAYS}`
        )
      );
    }

    return Promise.resolve();
  };

  const handleAfterOpenChange = useCallback(
    async (isOpen: boolean) => {
      if (!isOpen || !bot) return;

      const { details, bot: botData } = bot;
      const { TriggerType } = botData;
      const {
        RobotType,
        Comment,
        EventTimeShift,
        CustomTimeShift,
        TypeParameters,
        TriggerParameters,
        AfterExecutionTags,
        IgnoreSchedule,
      } = details;

      const triggerParams = TriggerParameters as
        | {
            ServiceIds?: number[];
            ClientServiceIds?: number[];
            CategoryIds?: number[];
            InternalMovement?: boolean;
            // Поля триггера "Поиск клиентов через ИИ" (AiClientSearch),
            // как и все остальные TriggerParameters — PascalCase.
            SearchPrompt?: string;
            ScanPeriod?: number;
            CurrentFunnelOnly?: boolean;
            // Триггер "Смена тега" (TagIsSet): TagFilters — новый формат,
            // Tags + HasOrRelation — у роботов, сохранённых до него.
            TagFilters?: RobotTagFilter[];
            Tags?: number[];
            HasOrRelation?: boolean;
          }
        | undefined;

      const triggerServiceIds =
        triggerParams?.ServiceIds ?? triggerParams?.ClientServiceIds;

      const triggerCategoryIds = triggerParams?.CategoryIds;
      const triggerInternalMovement =
        triggerParams?.InternalMovement ?? triggerParams?.CurrentFunnelOnly;
      let customDeadlineDay: string | undefined;
      let customDeadlineTime: moment.Moment | undefined;

      if (CustomTimeShift && CustomTimeShift !== '00:00:00') {
        if (CustomTimeShift.includes('.')) {
          const [daysPart, timePart] = CustomTimeShift.split('.');
          customDeadlineDay = daysPart;
          customDeadlineTime = moment(timePart, 'HH:mm:ss');
        } else {
          customDeadlineDay = '0';
          customDeadlineTime = moment(CustomTimeShift, 'HH:mm:ss');
        }
      }

      let taskCustomDeadlineDay: string | undefined;
      let taskCustomDeadlineTime: moment.Moment | undefined;

      if (
        TypeParameters &&
        TypeParameters.CustomTimeDeadline &&
        TypeParameters.CustomTimeDeadline !== '00:00:00'
      ) {
        const string = String(TypeParameters.CustomTimeDeadline);
        if (string.includes('.')) {
          const [daysPart, timePart] = string.split('.');
          taskCustomDeadlineDay = daysPart;
          taskCustomDeadlineTime = moment(timePart, 'HH:mm:ss');
        } else {
          taskCustomDeadlineDay = '0';
          taskCustomDeadlineTime = moment(string, 'HH:mm:ss');
        }
      }

      if (
        (selectedBotTypeId === SEND_MESSAGE_ROBOT_INDEX ||
          selectedBotTypeId === KEYWORD_MESSAGE_ROBOT_INDEX) &&
        TypeParameters?.AttachmentsIds &&
        Array.isArray(TypeParameters.AttachmentsIds) &&
        TypeParameters.AttachmentsIds.length
      ) {
        const adaptedFiles = selectedBotFiles
          .filter(({ Id }) =>
            (TypeParameters.AttachmentsIds as number[]).includes(Id)
          )
          .map(({ Id, Name }) => {
            return {
              uid: String(Id),
              name: Name,
              status: 'done' as const,
            };
          });
        form.setFieldValue('files', adaptedFiles);
      }

      // У «Установки тега» с триггером IncomingMessage ключевые слова и канал —
      // условия триггера (TriggerParameters), у «Сообщения по ключевым
      // словам» — параметры действия (TypeParameters).
      const isSetTagIncomingMessage =
        String(RobotType) === SET_TAG_ROBOT_INDEX &&
        TriggerType === INCOMING_MESSAGE_TRIGGER_TYPE;
      const channelParams = isSetTagIncomingMessage
        ? TriggerParameters
        : TypeParameters;

      const hasChannel =
        channelParams?.SocialIntegrationType ||
        channelParams?.SocialIntegrationType === null;
      const statusValue =
        Array.isArray(TriggerParameters?.RecordStatuses) &&
        TriggerParameters.RecordStatuses.length > 0
          ? TriggerParameters.RecordStatuses[0]
          : TriggerParameters?.Status;

      form.setFieldsValue({
        trigger: TriggerType,
        comment: Comment,
        afterExecutionTags: AfterExecutionTags?.map(({ Id }) => Id),
        ...((EventTimeShift || EventTimeShift === 0) && {
          deadline: EventTimeShift,
        }),
        ...((TypeParameters?.TimeDeadlineTimeShift ||
          TypeParameters?.TimeDeadlineTimeShift === 0) && {
          task_deadline: TypeParameters.TimeDeadlineTimeShift,
        }),
        ...(customDeadlineDay && { custom_deadline_day: customDeadlineDay }),
        ...(customDeadlineTime && { custom_deadline_time: customDeadlineTime }),
        ...(taskCustomDeadlineDay && {
          task_custom_deadline_day: taskCustomDeadlineDay,
        }),
        ...(taskCustomDeadlineTime && {
          task_custom_deadline_time: taskCustomDeadlineTime,
        }),
        ...(TypeParameters?.SaleFunnelId && {
          funnel: TypeParameters.SaleFunnelId,
        }),
        ...(TypeParameters?.IsNeedRemovePrevFunnel && {
          isNeedRemovePrevFunnel: TypeParameters.IsNeedRemovePrevFunnel,
        }),
        ...(TypeParameters?.StageToSwitchId && {
          funnelStage: String(TypeParameters.StageToSwitchId),
        }),
        ...(TypeParameters?.Description && {
          description: TypeParameters.Description,
        }),
        ...{
          ownerId:
            TypeParameters?.OwnerId === null
              ? LAST_OWNER
              : TypeParameters?.OwnerId,
        },
        ...(statusValue !== undefined &&
          statusValue !== null && {
            status: String(statusValue),
          }),
        ...(triggerServiceIds && {
          services: triggerServiceIds,
        }),
        ...(triggerCategoryIds && {
          serviceCategories: triggerCategoryIds,
        }),
        ...(typeof triggerInternalMovement === 'boolean' && {
          internalMovement: triggerInternalMovement,
        }),
        ...(triggerParams?.SearchPrompt !== undefined && {
          searchPrompt: triggerParams.SearchPrompt,
        }),
        ...(triggerParams?.ScanPeriod !== undefined && {
          scanPeriod: triggerParams.ScanPeriod,
        }),
        ...(TriggerType === 'TagIsSet' &&
          triggerParams && { tags: mapTriggerTagsFromApi(triggerParams) }),
        ...(TypeParameters?.TagIds && {
          setTagIds: TypeParameters.TagIds,
        }),
        ...(typeof TypeParameters?.IsNeedAdd === 'boolean' && {
          isNeedAdd: TypeParameters.IsNeedAdd,
        }),
        ...(TypeParameters?.ConflictingTagIds && {
          conflictingTagIds: TypeParameters.ConflictingTagIds,
        }),
        ignoreSchedule: Boolean(IgnoreSchedule),
        ...(TriggerParameters?.EmployeesIds && {
          employees: TriggerParameters.EmployeesIds,
        }),
        ...(TriggerParameters?.SocialType && {
          socialType: TriggerParameters.SocialType,
        }),

        ...(TypeParameters?.Message && {
          message: TypeParameters.Message,
        }),
        ...(TypeParameters?.Buttons && {
          buttons: TypeParameters.Buttons,
        }),
        ...(channelParams?.KeywordGroups && {
          keywordGroups: channelParams.KeywordGroups,
        }),
        ...(typeof TypeParameters?.IsStopAfterFirstResponse === 'boolean' && {
          isStopAfterFirstResponse: TypeParameters.IsStopAfterFirstResponse,
        }),
        // Ставим всегда, а не условным spread: у робота без текста поле
        // должно быть пустым, а не со значением прошлого робота
        commentReplyText:
          typeof TypeParameters?.CommentReplyText === 'string'
            ? TypeParameters.CommentReplyText
            : undefined,
        // Тоже всегда: выбор публикаций прошлого робота не переносится
        ...getPublicationSelectionFormValues(TypeParameters),
        ...(TypeParameters?.BranchIds && {
          branch: TypeParameters.BranchIds,
        }),
        ...(hasChannel && {
          socialIntegrationType:
            channelParams.SocialIntegrationType ?? DEFAULT_CHANNEL_INDEX,
        }),
        ...('SocialIntegrationId' in (channelParams || {}) && {
          socialIntegrationId:
            channelParams!.SocialIntegrationId === null
              ? DEFAULT_SOCIAL_INTEGRATION_ID_INDEX
              : channelParams!.SocialIntegrationId,
        }),
        ...(TypeParameters?.WeekSchedule && {
          schedule: setScheduleFormData(
            TypeParameters.WeekSchedule as ServerScheduleData
          ),
        }),
        useSchedule: false,
      });
      setSelectedDeadlineItem(EventTimeShift);
      setTaskSelectedDeadlineItem(
        Number(TypeParameters?.TimeDeadlineTimeShift)
      );

      handleChannelOptions('socialIntegrationType');
      setSelectedTriggerId(TriggerType);
    },
    [
      bot,
      form,
      handleChannelOptions,
      selectedBotFiles,
      selectedBotTypeId,
      setScheduleFormData,
    ]
  );

  useEffect(() => {
    if (!isOpen || !bot || !isRobotReplyEnabled) return;

    const { AiPrompt } = bot.details;

    if (AiPrompt) {
      form.setFieldsValue({ aiPrompt: AiPrompt, messageMode: MESSAGE_MODE_AI });
    }
  }, [isOpen, bot, isRobotReplyEnabled, form]);

  useEffect(() => {
    if (!isOpen || isLoading || !fieldsToDisplay?.length) {
      return;
    }

    if (form.getFieldValue('socialIntegrationType')) {
      handleChannelOptions('socialIntegrationType');
    }
  }, [
    fieldsToDisplay,
    form,
    handleChannelOptions,
    isLoading,
    isOpen,
    socialIntegrations,
  ]);

  // Публикации принадлежат аккаунту Instagram: при смене канала или
  // интеграции выбор прошлого аккаунта сбрасывается
  const resetInstagramPublicationsOnChannelChange = useCallback(
    (changedValues: Partial<BotDetailsFormInterface>) => {
      if (
        selectedBotTypeId === KEYWORD_MESSAGE_ROBOT_INDEX &&
        ('socialIntegrationType' in changedValues ||
          'socialIntegrationId' in changedValues)
      ) {
        form.setFieldsValue({ instagramPublicationIds: [] });
      }
    },
    [form, selectedBotTypeId]
  );

  const handleScheduleChange = (e: CheckboxChangeEvent) => {
    setShowSchedule(e.target.checked);
  };

  const handleSalesFunnelChange = useCallback(
    (salesFunnelId: number) => {
      if (selectedBotTypeId === TRANSITION_FROM_THE_SALES_FUNNEL_ROBOT_INDEX)
        getSalesFunnelStagesListHandler(salesFunnelId);
    },
    [getSalesFunnelStagesListHandler, selectedBotTypeId]
  );

  const handleBranchChange = (value: number[]) => {
    if (value.length) {
      const filteredIntegrations =
        socialIntegrations?.filter(({ branchId }) =>
          value.includes(branchId)
        ) || [];
      setIntegrationsByBranch(filteredIntegrations);

      form.resetFields(['socialIntegrationType', 'socialIntegrationId']);
    } else {
      setIntegrationsByBranch(socialIntegrations || []);

      form.resetFields(['socialIntegrationType', 'socialIntegrationId']);
    }
  };

  return {
    getBotDetailsModalTitle,
    handleCancel,
    submit,
    isAddButtonLoading,
    handleAfterOpenChange,
    form,
    setSelectedTriggerId,
    addButtonTooltip,
    fieldsToDisplay,
    initialScheduleValues,
    handleTriggerChange,
    handleSalesFunnelChange,
    handleChannelOptions,
    handleDeadlineChange,
    selectedDeadlineItem,
    validateNumericInput,
    onCustomDayInput,
    filteredIntegrations,
    handleScheduleChange,
    showSchedule,
    handleBranchChange,
    selectedTriggerId,
    taskSelectedDeadlineItem,
    handleTaskDeadlineChange,
    tempTime,
    setTempTime,
    taskTempTime,
    setTaskTempTime,
    isRobotReplyEnabled,
    isSetTagIncomingMessageRobot,
    isNeedAddValue,
    conflictingTagIdsRules,
    resetInstagramPublicationsOnChannelChange,
    overlapCheckGateRef,
    windowSessionId,
    stopOverlapChecks,
  };
};
